import React, { useEffect, useState } from 'react';
import { Mail, Phone, Briefcase, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { apiJson, messageFrom, codeFrom, TOKEN_KEY } from '../lib/account';

const inputCls = 'mt-1.5 bg-white/[0.03] border-white/10 text-white placeholder-gray-600 focus:border-orange-500 focus:ring-orange-500 rounded-xl';
const btnCls = 'w-full rounded-full py-3 text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 disabled:opacity-50 flex items-center justify-center';

const Card = ({ icon: Icon, title, open, onToggle, testid, children }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02]">
    <button type="button" data-testid={testid} onClick={onToggle} className="w-full flex items-center justify-between px-4 py-4 text-left">
      <span className="flex items-center gap-3 text-sm text-white"><Icon className="h-4 w-4 text-orange-400" />{title}</span>
      <span className="text-xs text-orange-400">{open ? 'Close' : 'Change'}</span>
    </button>
    {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
  </div>
);

const Msg = ({ kind, children, testid }) => (children ? <p data-testid={testid} className={`text-sm ${kind === 'error' ? 'text-red-400' : 'text-emerald-400'}`}>{children}</p> : null);

// Change email (link to the NEW address), change phone (SMS code), and the company name / billing note printed on receipts.
const AccountSettings = ({ user }) => {
  const [open, setOpen] = useState('');
  const toggle = (k) => setOpen(open === k ? '' : k);

  // ---- email
  const [newEmail, setNewEmail] = useState('');
  const [emailPw, setEmailPw] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMsg, setEmailMsg] = useState({ kind: '', text: '' });
  const submitEmail = async (e) => {
    e.preventDefault();
    setEmailBusy(true);
    setEmailMsg({ kind: '', text: '' });
    try {
      const r = await apiJson('/api/auth/change-email', 'POST', { new_email: newEmail.trim(), current_password: emailPw });
      if (r.ok) {
        setEmailMsg({ kind: 'ok', text: `We sent a confirmation link to ${r.body.newEmailMasked || 'the new address'}. Your email changes when you open it, and you will be signed out everywhere.` });
        setEmailPw('');
      } else {
        setEmailMsg({ kind: 'error', text: messageFrom(r.body, 'We could not start the change. Please try again.') });
      }
    } catch (err) {
      setEmailMsg({ kind: 'error', text: 'We could not reach RideBuraq. Check your connection and try again.' });
    } finally {
      setEmailBusy(false);
    }
  };

  // ---- phone
  const [phone, setPhone] = useState('');
  const [phonePw, setPhonePw] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneMsg, setPhoneMsg] = useState({ kind: '', text: '' });
  const sendCode = async (e) => {
    e.preventDefault();
    setPhoneBusy(true);
    setPhoneMsg({ kind: '', text: '' });
    try {
      const r = await apiJson('/api/auth/phone/send-code', 'POST', { phone: phone.trim(), current_password: phonePw || undefined });
      if (r.ok && r.body.sent) {
        setCodeSent(true);
        setPhoneMsg({ kind: 'ok', text: `We texted a 6-digit code to the number ending ${r.body.phoneLast4}.` });
      } else if (r.ok && r.body.alreadyVerified) {
        setPhoneMsg({ kind: 'ok', text: 'That is already your verified number.' });
      } else {
        setPhoneMsg({ kind: 'error', text: messageFrom(r.body, 'We could not send the code. Please try again.') });
      }
    } catch (err) {
      setPhoneMsg({ kind: 'error', text: 'We could not reach RideBuraq. Check your connection and try again.' });
    } finally {
      setPhoneBusy(false);
    }
  };
  const verifyCode = async (e) => {
    e.preventDefault();
    setPhoneBusy(true);
    setPhoneMsg({ kind: '', text: '' });
    try {
      const r = await apiJson('/api/auth/phone/verify', 'POST', { code: code.trim() });
      if (r.ok) {
        // A changed number signs out the other devices; the server hands this one a fresh token.
        if (r.body.accessToken) { try { localStorage.setItem(TOKEN_KEY, r.body.accessToken); } catch (err) { /* ignore */ } }
        setPhoneMsg({ kind: 'ok', text: 'Your phone number has been updated.' });
        setTimeout(() => window.location.reload(), 900);
      } else {
        setPhoneMsg({ kind: 'error', text: messageFrom(r.body, 'That code did not work. Please try again.') });
      }
    } catch (err) {
      setPhoneMsg({ kind: 'error', text: 'We could not reach RideBuraq. Check your connection and try again.' });
    } finally {
      setPhoneBusy(false);
    }
  };
  const needsPassword = !!(user && user.phoneVerified);

  // ---- billing details for receipts
  const [company, setCompany] = useState('');
  const [note, setNote] = useState('');
  const [billBusy, setBillBusy] = useState(false);
  const [billMsg, setBillMsg] = useState({ kind: '', text: '' });
  useEffect(() => {
    let alive = true;
    apiJson('/api/auth/me/billing-profile').then((r) => {
      if (alive && r.ok) { setCompany(r.body.companyName || ''); setNote(r.body.billingNote || ''); }
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const saveBilling = async (e) => {
    e.preventDefault();
    setBillBusy(true);
    setBillMsg({ kind: '', text: '' });
    try {
      const r = await apiJson('/api/auth/me/billing-profile', 'PUT', { company_name: company, billing_note: note });
      setBillMsg(r.ok ? { kind: 'ok', text: 'Saved. It will appear on your receipts.' } : { kind: 'error', text: messageFrom(r.body, 'Could not save.') });
    } catch (err) {
      setBillMsg({ kind: 'error', text: 'We could not reach RideBuraq.' });
    } finally {
      setBillBusy(false);
    }
  };

  return (
    <div className="space-y-3 mb-6">
      <p className="text-[11px] tracking-[0.3em] uppercase text-gray-500">Account</p>

      <Card icon={Mail} title="Change email" open={open === 'email'} onToggle={() => toggle('email')} testid="change-email-toggle">
        <form onSubmit={submitEmail} className="space-y-3">
          <p className="text-xs text-gray-400">Current: {user?.email}. We email a link to the new address; nothing changes until you open it.</p>
          <div>
            <Label htmlFor="new-email" className="text-gray-400 text-xs tracking-wide uppercase">New email</Label>
            <Input id="new-email" type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className={inputCls} placeholder="new@example.com" />
          </div>
          <div>
            <Label htmlFor="email-pw" className="text-gray-400 text-xs tracking-wide uppercase">Current password</Label>
            <Input id="email-pw" type="password" required autoComplete="current-password" value={emailPw} onChange={(e) => setEmailPw(e.target.value)} className={inputCls} />
          </div>
          <Msg kind={emailMsg.kind} testid="change-email-msg">{emailMsg.text}</Msg>
          <button type="submit" data-testid="change-email-submit" disabled={emailBusy} className={btnCls}>
            {emailBusy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</> : 'Send confirmation link'}
          </button>
        </form>
      </Card>

      <Card icon={Phone} title="Change phone number" open={open === 'phone'} onToggle={() => toggle('phone')} testid="change-phone-toggle">
        {!codeSent ? (
          <form onSubmit={sendCode} className="space-y-3">
            <p className="text-xs text-gray-400">{user?.phone ? `Current: ${user.phone}. ` : ''}We text a code to the new number to confirm it.</p>
            <div>
              <Label htmlFor="new-phone" className="text-gray-400 text-xs tracking-wide uppercase">New phone number</Label>
              <Input id="new-phone" type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} placeholder="(555) 123-4567" />
            </div>
            {needsPassword && (
              <div>
                <Label htmlFor="phone-pw" className="text-gray-400 text-xs tracking-wide uppercase">Current password</Label>
                <Input id="phone-pw" type="password" required autoComplete="current-password" value={phonePw} onChange={(e) => setPhonePw(e.target.value)} className={inputCls} />
              </div>
            )}
            <Msg kind={phoneMsg.kind} testid="change-phone-msg">{phoneMsg.text}</Msg>
            <button type="submit" data-testid="change-phone-send" disabled={phoneBusy} className={btnCls}>
              {phoneBusy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</> : 'Text me a code'}
            </button>
          </form>
        ) : (
          <form onSubmit={verifyCode} className="space-y-3">
            <div>
              <Label htmlFor="phone-code" className="text-gray-400 text-xs tracking-wide uppercase">6-digit code</Label>
              <Input id="phone-code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value)} className={inputCls} placeholder="123456" />
            </div>
            <Msg kind={phoneMsg.kind} testid="change-phone-msg">{phoneMsg.text}</Msg>
            <button type="submit" data-testid="change-phone-verify" disabled={phoneBusy} className={btnCls}>
              {phoneBusy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Checking...</> : 'Confirm number'}
            </button>
            <button type="button" onClick={() => { setCodeSent(false); setCode(''); setPhoneMsg({ kind: '', text: '' }); }} className="w-full text-xs text-gray-400 hover:text-white">Use a different number</button>
          </form>
        )}
      </Card>

      <Card icon={Briefcase} title="Billing details on receipts" open={open === 'billing'} onToggle={() => toggle('billing')} testid="billing-toggle">
        <form onSubmit={saveBilling} className="space-y-3">
          <p className="text-xs text-gray-400">Optional. For business riders: printed on your receipts.</p>
          <div>
            <Label htmlFor="company" className="text-gray-400 text-xs tracking-wide uppercase">Company name</Label>
            <Input id="company" maxLength={120} value={company} onChange={(e) => setCompany(e.target.value)} className={inputCls} placeholder="Acme Corp" />
          </div>
          <div>
            <Label htmlFor="billing-note" className="text-gray-400 text-xs tracking-wide uppercase">Billing note</Label>
            <Input id="billing-note" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} placeholder="PO number, cost center, project..." />
          </div>
          <Msg kind={billMsg.kind} testid="billing-msg">{billMsg.text}</Msg>
          <button type="submit" data-testid="billing-save" disabled={billBusy} className={btnCls}>
            {billBusy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : 'Save'}
          </button>
        </form>
      </Card>
    </div>
  );
};

export default AccountSettings;
